import Vue from 'vue'
import { isPlainObject } from 'lodash'
import store from '@/vuex/store'
import Sentry, { featureTags } from '@/services/sentry'
import TeamAttributesApi from '@/api/teamAttributes'
import { USER_PREFERENCE_REGISTRY } from '@/business-logic/user-preferences/userPreferenceRegistry'
import {
  ADVANCED_SETTINGS_SECTION_FLAG,
  USER_PREFERENCE_TYPES,
  findUserPreferenceDefinition,
  resolveUserPreferenceValue,
} from '@/business-logic/user-preferences/userPreferenceValues'
import { TEAM_ADVANCED_SETTING_REGISTRY } from '@/business-logic/team-advanced-settings/teamAdvancedSettingRegistry'

export const TEAM_POLICIES_ATTRIBUTE = 'advancedSettingPolicies'

export const TEAM_POLICIES = Object.freeze({
  ALWAYS_ON: 'alwaysOn',
  ALWAYS_OFF: 'alwaysOff',
  MEMBER_CHOICE: 'memberChoice',
  DEFAULT_ON: 'defaultOn',
  DEFAULT_OFF: 'defaultOff',
})

const LOCKED_VALUE_BY_POLICY = Object.freeze({
  [TEAM_POLICIES.ALWAYS_ON]: true,
  [TEAM_POLICIES.ALWAYS_OFF]: false,
})

const DEFAULT_VALUE_BY_POLICY = Object.freeze({
  [TEAM_POLICIES.DEFAULT_ON]: true,
  [TEAM_POLICIES.DEFAULT_OFF]: false,
})

export const TEAM_ADVANCED_SETTINGS_SENTRY_CONTEXT = Object.freeze({
  tags: featureTags({ featureFlag: ADVANCED_SETTINGS_SECTION_FLAG, featureName: 'team-advanced-settings', taskId: 'CORE-46' }),
})

const state = Vue.observable({ policiesByTeam: {} })
const loadsByTeam = new Map()
let saveQueue = Promise.resolve()

const currentTeamId = () => store.state.currentUser?.accountId

const isTeamAdvancedSetting = (key) => TEAM_ADVANCED_SETTING_REGISTRY.some((definition) => definition.key === key)

const storeTeamPolicies = (teamId, policies) => {
  Vue.set(state.policiesByTeam, teamId, isPlainObject(policies) ? { ...policies } : {})
}

// A key missing from the policies object means memberChoice.
export const loadTeamAdvancedSettingPolicies = ({ refresh = false } = {}) => {
  const teamId = currentTeamId()
  if (!teamId) return Promise.resolve({})
  if (!refresh && loadsByTeam.has(teamId)) return loadsByTeam.get(teamId)
  const load = TeamAttributesApi.getTeamAttributes().then((attributes) => {
    // A superseded load resolving late must not overwrite a newer load or an optimistic save.
    if (loadsByTeam.get(teamId) === load) storeTeamPolicies(teamId, attributes?.[TEAM_POLICIES_ATTRIBUTE])
    return state.policiesByTeam[teamId]
  })
  loadsByTeam.set(teamId, load)
  return load
}

// Fails open to memberChoice and never awaits the load, so it is safe to call from a computed property.
export const getTeamAdvancedSettingPolicy = (key) => {
  if (!isTeamAdvancedSetting(key)) return TEAM_POLICIES.MEMBER_CHOICE
  const teamId = currentTeamId()
  if (teamId && !loadsByTeam.has(teamId)) {
    loadTeamAdvancedSettingPolicies().catch((error) => {
      Sentry.captureException(error, TEAM_ADVANCED_SETTINGS_SENTRY_CONTEXT)
    })
  }
  const policy = state.policiesByTeam[teamId]?.[key]
  return Object.values(TEAM_POLICIES).includes(policy) ? policy : TEAM_POLICIES.MEMBER_CHOICE
}

// Owners and admins are bound too, as with the team AI preference locks.
export const getTeamLockedPreferenceValue = (key) => LOCKED_VALUE_BY_POLICY[getTeamAdvancedSettingPolicy(key)]

export const isUserPreferenceLockedByTeam = (definition) => definition.type === USER_PREFERENCE_TYPES.BOOLEAN
  && getTeamLockedPreferenceValue(definition.key) !== undefined

const getTeamDefaultPreferenceValue = (key) => DEFAULT_VALUE_BY_POLICY[getTeamAdvancedSettingPolicy(key)]

// The team default stands in for the registry default only, so a member's own saved value still wins.
export const withTeamDefaultPreference = (definition) => {
  if (definition.type !== USER_PREFERENCE_TYPES.BOOLEAN) return definition
  const teamDefault = getTeamDefaultPreferenceValue(definition.key)
  return teamDefault === undefined ? definition : { ...definition, default: teamDefault }
}

const getUserPreferenceWithTeamDefault = (key) => {
  const definition = findUserPreferenceDefinition(USER_PREFERENCE_REGISTRY, key)
  if (!definition) return undefined
  const preferences = store.state.currentUser?.preferences
  return resolveUserPreferenceValue(withTeamDefaultPreference(definition), preferences, store.getters)
}

/**
 * The value a member gets for a registered user preference under the team's policy: the team lock, else the
 * member's saved value, else the team default, else the registry default. Undefined for an unregistered key.
 */
export const getUserPreferenceUnderTeamPolicy = (key) => {
  const lockedValue = getTeamLockedPreferenceValue(key)
  return lockedValue === undefined ? getUserPreferenceWithTeamDefault(key) : lockedValue
}

const persistTeamAdvancedSettingPolicy = async (key, policy) => {
  const teamId = currentTeamId()
  await loadTeamAdvancedSettingPolicies()
  const previousPolicies = state.policiesByTeam[teamId] || {}
  const nextPolicies = { ...previousPolicies }
  if (policy === TEAM_POLICIES.MEMBER_CHOICE) delete nextPolicies[key]
  else nextPolicies[key] = policy
  storeTeamPolicies(teamId, nextPolicies)
  try {
    const attributes = await TeamAttributesApi.patchTeamAttributes({ [TEAM_POLICIES_ATTRIBUTE]: nextPolicies })
    const savedPolicies = attributes?.[TEAM_POLICIES_ATTRIBUTE]
    if (isPlainObject(savedPolicies)) storeTeamPolicies(teamId, savedPolicies)
  } catch (error) {
    storeTeamPolicies(teamId, previousPolicies)
    throw error
  }
}

// Saves are serialised because each one writes the team's whole policies object.
export const saveTeamAdvancedSettingPolicy = (key, policy) => {
  if (!isTeamAdvancedSetting(key)) return Promise.reject(new Error(`Unknown team advanced setting "${key}"`))
  if (!Object.values(TEAM_POLICIES).includes(policy)) {
    return Promise.reject(new Error(`Invalid policy for team advanced setting "${key}"`))
  }
  const queued = saveQueue.catch(() => {}).then(() => persistTeamAdvancedSettingPolicy(key, policy))
  saveQueue = queued
  return queued
}
